/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {DownloadLine, SearchLine} from '@dropbox/dig-icons/assets';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router';

import {buildRawCsv, downloadCsv} from './aiQueryCsv';

export type JobFamilyCatalogItem = {
  job_family_id: string | null;
  job_family: string | null;
  employee_count: number;
  distinct_title_count: number;
  top_titles: {title: string; employee_count: number}[];
  distinct_cost_center_function_count: number;
  top_cost_center_functions: {cost_center_function: string; employee_count: number}[];
  label_conflict: boolean;
};

type JobFamilyCatalogResponse = {
  generated_at: string;
  active_employee_count: number;
  complete_job_family_count: number;
  missing_job_family_count: number;
  distinct_job_family_id_count: number;
  conflicting_job_family_id_count: number;
  job_families: JobFamilyCatalogItem[];
};

const fetchJobFamilies = async (): Promise<JobFamilyCatalogResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/admin/dcd-job-families',
  }) as Promise<JobFamilyCatalogResponse>;

const compareNullableStrings = (left: string | null, right: string | null) =>
  (left ?? '').localeCompare(right ?? '');

const columns: Column<JobFamilyCatalogItem>[] = [
  {
    type: 'Job family',
    fixed: true,
    minWidth: 320,
    sort: (a, b) => compareNullableStrings(a.job_family, b.job_family),
  },
  {
    type: 'DCD family ID',
    minWidth: 300,
    sort: (a, b) => compareNullableStrings(a.job_family_id, b.job_family_id),
  },
  {
    type: 'People',
    minWidth: 100,
    sort: (a, b) => a.employee_count - b.employee_count,
  },
  {
    type: 'Top titles',
    minWidth: 420,
    sort: (a, b) => a.distinct_title_count - b.distinct_title_count,
  },
  {
    type: 'Cost center functions',
    minWidth: 320,
    sort: (a, b) => a.distinct_cost_center_function_count - b.distinct_cost_center_function_count,
  },
  {type: 'Status', minWidth: 160},
];

const getStatus = (item: JobFamilyCatalogItem) => {
  if (!item.job_family_id || !item.job_family) return 'Incomplete';
  if (item.label_conflict) return 'Label conflict';
  return 'Complete';
};

const formatTitleCounts = (item: JobFamilyCatalogItem) =>
  item.top_titles
    .map(({title, employee_count}) => `${title} (${employee_count.toLocaleString()})`)
    .join('; ');

const formatCostCenterFunctionCounts = (item: JobFamilyCatalogItem) =>
  item.top_cost_center_functions
    .map(
      ({cost_center_function, employee_count}) =>
        `${cost_center_function} (${employee_count.toLocaleString()})`
    )
    .join('; ');

export const buildJobFamilyCsv = (items: JobFamilyCatalogItem[]) =>
  buildRawCsv(
    [
      'job_family_id',
      'job_family',
      'employee_count',
      'distinct_title_count',
      'top_titles',
      'distinct_cost_center_function_count',
      'top_cost_center_functions',
      'status',
    ],
    items.map((item) => [
      item.job_family_id ?? '',
      item.job_family ?? '',
      String(item.employee_count),
      String(item.distinct_title_count),
      formatTitleCounts(item),
      String(item.distinct_cost_center_function_count),
      formatCostCenterFunctionCounts(item),
      getStatus(item),
    ])
  );

const JobFamilyRow = ({item}: {item: JobFamilyCatalogItem}) => {
  const status = getStatus(item);
  return (
    <Table.Row>
      <Table.Cell>
        <Text color={item.job_family ? 'standard' : 'subtle'}>
          {item.job_family ?? 'Missing label'}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text color={item.job_family_id ? 'standard' : 'subtle'}>
          {item.job_family_id ?? 'Missing ID'}
        </Text>
      </Table.Cell>
      <Table.Cell>{item.employee_count.toLocaleString()}</Table.Cell>
      <Table.Cell>
        <Text color={item.top_titles.length ? 'standard' : 'subtle'}>
          {formatTitleCounts(item) || 'No title data'}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text color={item.top_cost_center_functions.length ? 'standard' : 'subtle'}>
          {formatCostCenterFunctionCounts(item) || 'No cost center function data'}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text color={status === 'Complete' ? 'subtle' : 'error'}>{status}</Text>
      </Table.Cell>
    </Table.Row>
  );
};

export const AdminDcdJobFamilies = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const catalogMutation = useMutation({mutationFn: fetchJobFamilies});
  const catalog = catalogMutation.data;

  useDocumentTitle('DCD job families');

  const filteredItems = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase();
    if (!query) return catalog?.job_families ?? [];
    return (catalog?.job_families ?? []).filter((item) =>
      [
        item.job_family,
        item.job_family_id,
        ...item.top_titles.map(({title}) => title),
        ...item.top_cost_center_functions.map(({cost_center_function}) => cost_center_function),
      ].some((value) => value?.toLocaleLowerCase().includes(query))
    );
  }, [catalog?.job_families, filter]);

  if (!pulseUser) return null;
  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  const exportCatalog = () => {
    if (!catalog) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(buildJobFamilyCsv(catalog.job_families), `dcd-job-families-${stamp}.csv`);
  };

  return (
    <Layout.Container
      width="100%"
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'DCD job families', to: ROUTE_PATHS.ADMIN_DCD_JOB_FAMILIES},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>DCD job families</Title>

        <Split alignY="center" gap="12" wrap="wrap">
          <Split.Item>
            <Button
              variant="primary"
              onClick={() => catalogMutation.mutate()}
              isLoading={catalogMutation.isPending}
              disabled={catalogMutation.isPending}
              withIconStart={<UIIcon src={SearchLine} />}
            >
              Fetch from DCD
            </Button>
          </Split.Item>
          <Split.Item>
            <Button
              variant="outline"
              onClick={exportCatalog}
              disabled={!catalog}
              withIconStart={<UIIcon src={DownloadLine} />}
            >
              Export CSV
            </Button>
          </Split.Item>
          <Split.Item>
            <Box style={{minWidth: 240, maxWidth: 420}}>
              <TextInput
                aria-label="Filter job families"
                placeholder="Filter families, titles, or functions"
                value={filter}
                onChange={(event) => setFilter(event.currentTarget.value)}
                disabled={!catalog}
              />
            </Box>
          </Split.Item>
        </Split>

        {catalogMutation.error instanceof Error ? (
          <SystemBanner tone="alert">
            <Text>{catalogMutation.error.message || 'Unable to fetch DCD job families.'}</Text>
          </SystemBanner>
        ) : null}

        {catalog ? (
          <>
            <Split gap="24" wrap="wrap">
              <Text>{catalog.distinct_job_family_id_count.toLocaleString()} family IDs</Text>
              <Text>{catalog.active_employee_count.toLocaleString()} active people</Text>
              <Text>{catalog.complete_job_family_count.toLocaleString()} complete records</Text>
              <Text color={catalog.missing_job_family_count ? 'error' : 'subtle'}>
                {catalog.missing_job_family_count.toLocaleString()} missing ID or label
              </Text>
              <Text color={catalog.conflicting_job_family_id_count ? 'error' : 'subtle'}>
                {catalog.conflicting_job_family_id_count.toLocaleString()} conflicting IDs
              </Text>
              <Text color="subtle">Fetched {new Date(catalog.generated_at).toLocaleString()}</Text>
            </Split>

            <Table
              columns={columns}
              data={filteredItems}
              sortConfig={{columnId: 'People', asc: false}}
              compact
            >
              <Table.Header />
              <Table.Body>
                {filteredItems.length ? (
                  <Table.Rows renderRow={(item) => <JobFamilyRow item={item} />} />
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan={columns.length}>
                      <Text color="subtle">No matching job families.</Text>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </>
        ) : catalogMutation.isPending ? (
          <Box display="flex" alignItems="center" style={{gap: 8}}>
            <Spinner size="small" />
            <Text color="subtle">Fetching DCD job families</Text>
          </Box>
        ) : null}
      </Stack>
    </Layout.Container>
  );
};
